if (!customElements.get('wlsn-confetti')) {
  class WLSNConfetti extends HTMLElement {
    constructor() {
      super();

      this.colors = ['#4F77B9', '#6D8FD6', '#899FBD', '#5B86D9'];
      this.bits = [];
      this.rafId = null;
      this.isRunning = false;
    }

    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      if (!this.canvas || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const colors = (this.dataset.colors || '').split(',').map((color) => color.trim()).filter(Boolean);
      if (colors.length) this.colors = colors;
      this.accentColor = this.dataset.accent || '#FAD000';
      // Pieces per 100,000px² of area, unless fixed counts are set
      this.density = parseFloat(this.dataset.density) || 4;
      this.countMobile = parseInt(this.dataset.countMobile, 10) || 0;
      this.countDesktop = parseInt(this.dataset.countDesktop, 10) || 0;
      // "right" spawns most pieces over the right side on desktop
      this.spawnBias = this.dataset.spawnBias || '';

      this.ctx = this.canvas.getContext('2d');
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);

      this.resizeCanvas();
      this.seed();

      this.resizeObserver = new ResizeObserver(() => {
        const previousCount = this.targetCount();
        this.resizeCanvas();
        if (previousCount !== this.targetCount()) this.seed();
      });
      this.resizeObserver.observe(this);

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.resume() : this.pause()));
      });
      this.observer.observe(this);

      this.onVisibilityChange = () => {
        if (document.hidden) this.pause();
        else if (this.isVisible()) this.resume();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.pause();
    }

    isVisible() {
      const rect = this.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    resizeCanvas() {
      const rect = this.getBoundingClientRect();
      this.width = rect.width;
      this.height = rect.height;
      this.canvas.width = this.width * this.dpr;
      this.canvas.height = this.height * this.dpr;
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    isMobile() {
      return this.width < 768;
    }

    targetCount() {
      const fixed = this.isMobile() ? this.countMobile : this.countDesktop;
      if (fixed) return fixed;

      const count = Math.round(((this.width * this.height) / 100000) * this.density);
      return Math.min(Math.max(count, 20), 220);
    }

    random(min, max) {
      return min + Math.random() * (max - min);
    }

    spawnX() {
      if (this.spawnBias !== 'right' || this.isMobile()) return this.random(0, this.width);
      return Math.random() < 0.8
        ? this.random(this.width * 0.45, this.width)
        : this.random(0, this.width * 0.45);
    }

    makeBit(x, y, burst = false) {
      const big = !burst && Math.random() < 0.08;
      const color = Math.random() < 0.18
        ? this.accentColor
        : this.colors[Math.floor(Math.random() * this.colors.length)];

      return {
        x,
        y,
        len: big ? this.random(90, 150) : this.random(12, 26),
        rot: this.random(0, Math.PI * 2),
        vr: this.random(-0.04, 0.04),
        vx: burst ? this.random(-5, 5) : this.random(-0.6, 0.6),
        vy: burst ? this.random(-7, -2) : this.random(1, 2.2),
        flip: this.random(0, Math.PI * 2),
        vf: this.random(0.04, 0.12),
        color,
        big,
        alpha: big ? 0.35 : this.random(0.7, 1),
        life: burst ? 1 : Infinity,
      };
    }

    seed() {
      const count = this.targetCount();
      this.bits = [];
      for (let i = 0; i < count; i++) {
        this.bits.push(this.makeBit(this.spawnX(), this.random(-this.height, this.height)));
      }
    }

    // x / y are relative to this element
    burst(x, y, count = 70) {
      if (!this.ctx) return;
      for (let i = 0; i < count; i++) this.bits.push(this.makeBit(x, y, true));
    }

    drawBit(bit) {
      const { ctx } = this;
      const scaleY = Math.cos(bit.flip);
      const length = bit.len;
      const thickness = length * 0.22;

      ctx.save();
      ctx.translate(bit.x, bit.y);
      ctx.rotate(bit.rot);
      ctx.scale(1, Math.abs(scaleY) < 0.15 ? 0.15 : scaleY);
      ctx.globalAlpha = bit.alpha * (bit.life === Infinity ? 1 : Math.max(bit.life, 0));
      if (bit.big) ctx.filter = 'blur(8px)';

      ctx.fillStyle = bit.color;
      ctx.beginPath();
      ctx.moveTo(-length / 2, 0);
      ctx.quadraticCurveTo(0, -thickness * 1.6, length / 2, 0);
      ctx.quadraticCurveTo(0, thickness * 0.4, -length / 2, 0);
      ctx.fill();
      ctx.restore();
    }

    render() {
      this.ctx.clearRect(0, 0, this.width, this.height);

      for (let i = this.bits.length - 1; i >= 0; i--) {
        const bit = this.bits[i];
        bit.x += bit.vx + Math.sin(bit.flip) * 0.4;
        bit.y += bit.vy;
        bit.rot += bit.vr;
        bit.flip += bit.vf;

        if (bit.life !== Infinity) {
          bit.vy += 0.12;
          bit.vx *= 0.98;
          bit.life -= 0.008;
          if (bit.life <= 0 || bit.y > this.height + 40) {
            this.bits.splice(i, 1);
            continue;
          }
        } else if (bit.y > this.height + 40) {
          bit.y = -40;
          bit.x = this.spawnX();
        }

        this.drawBit(bit);
      }

      this.rafId = requestAnimationFrame(() => this.render());
    }

    resume() {
      if (!this.ctx || this.isRunning) return;
      this.isRunning = true;
      this.rafId = requestAnimationFrame(() => this.render());
    }

    pause() {
      if (!this.isRunning) return;
      this.isRunning = false;
      cancelAnimationFrame(this.rafId);
    }
  }

  customElements.define('wlsn-confetti', WLSNConfetti);
}
